import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { useSelector, useDispatch } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import ChatUIToolbar from './ChatUIToolbar/ChatUIToolbar';
import { ChatUIContainer, ChatUIContent } from './ChatUI.styles';
import ChatUIMacrosTab from './ChatUIMacrosTab/ChatUIMacrosTab';
import {
  extractChatUIMacroCopiedForSelectedTicket,
  extractChatUIStatus,
  extractCommerceWizardRecommendationData,
  extractNumberOfIncomingMessages,
  extractPreviouslyRecommendedProducts,
} from 'extractFromState';
import { FETCH_CHAT_MACROS } from 'constants/reactQueryTimers';
import * as mlEvents from '../../analytics/mlEventAnalytics';
import { fetchChatMacros } from 'api/macroArticles';
import { getMLRecommendedProducts } from 'actions/creatorsAsync';
import { setChatProductRecMacroCopied, setChatUIStatus } from 'actions/chatUI';
import ChatUIProductsTab from './ChatUIProductsTab/ChatUIProductsTab';
import useInitialMount from 'customHooks/useInitialMount';
import { chatUITabs } from 'constants/chatUI';
import ChatUIEmptyTab from './ChatUIEmptyTab/ChatUIEmptyTab';
import { trackMacroWizardCategorySelected } from '../../analytics/searchEventAnalytics';
import * as util from '../../util';
import { setTagForTicket } from '../../actions/tools/TaggingActions';

const propTypes = {
  brand: PropTypes.string,
  conversationId: PropTypes.string.isRequired,
  simplrAccountId: PropTypes.string,
  user: PropTypes.object.isRequired,
  targetUserType: PropTypes.string.isRequired,
  onMacroPersistNotify: PropTypes.func.isRequired,
};

const ChatUI = ({ brand, conversationId, simplrAccountPtrId, user, targetUserType, onMacroPersistNotify }) => {
  const [recommendedMacros, setRecommendedMacros] = useState([]);
  const [selectedMacro, setSelectedMacro] = useState(null);

  const dispatch = useDispatch();
  const isInitialMount = useInitialMount();

  const chatUIStatus = useSelector((state) => extractChatUIStatus(state));
  const chatMacroWithTriggerCopiedOnCurrentTicket = useSelector((state) =>
    extractChatUIMacroCopiedForSelectedTicket(state)
  );
  const productsAlreadyRecommendedOnTicket = useSelector((state) => extractPreviouslyRecommendedProducts(state));
  // We need to check if recommended products exist in redux state so that the ChatUI knows if it can display productRec view
  const recommendedProducts = useSelector((state) => extractCommerceWizardRecommendationData(state));
  const recommendedProductsExist =
    get(recommendedProducts, 'toDisplay', false) &&
    (!isEmpty(recommendedProducts.crossSellProducts) ||
      !isEmpty(recommendedProducts.upSellProducts) ||
      !isEmpty(recommendedProducts.globalProducts));

  /** *** Aggregated Conditionals *****/

  const canShowProductRecs =
    recommendedProductsExist && chatMacroWithTriggerCopiedOnCurrentTicket && !productsAlreadyRecommendedOnTicket;

  const canShowMacros = !isEmpty(recommendedMacros) && !chatMacroWithTriggerCopiedOnCurrentTicket;

  const chatUIIsClosed = !chatUIStatus.isOpen && !chatUIStatus.isOnscreen;
  const chatUIIsOpen = chatUIStatus.isOpen && chatUIStatus.isOnscreen;
  const chatUIOnscreenClosed = !chatUIStatus.isOpen && chatUIStatus.isOnscreen;

  /** *** EFFECTS *****/
  useEffect(() => {
    if (isInitialMount) {
      // On the first mount, if there are recommended products, and a macro was previously copied where chatProductRecsEnaled was TRUE, and we have not already recommended a product, then we want to open the ChatUI to the products tab
      if (canShowProductRecs) {
        dispatch(setChatUIStatus({ isOpen: true, isOnscreen: true, activeTab: chatUITabs.productRecs.tabName }));
      }
    }
  }, [
    dispatch,
    chatMacroWithTriggerCopiedOnCurrentTicket,
    isInitialMount,
    recommendedProductsExist,
    productsAlreadyRecommendedOnTicket,
    canShowProductRecs,
  ]);

  // This is to make sure that the ChatUI is closed when the component unmounts
  useEffect(() => {
    return () => {
      return dispatch(setChatUIStatus({ isOpen: false, isOnscreen: false, activeTab: null }));
    };
  }, [dispatch]);

  // Recommended macros only change when the # of incoming messages changes. Therefore we can pull macro recs from the cache if the numOfIncMessages has not changed
  const numOfIncMessages = useSelector((state) => extractNumberOfIncomingMessages(state));
  const macroQuery = useQuery(
    ['fetchChatMacros', brand, simplrAccountPtrId, conversationId, numOfIncMessages],
    () => fetchChatMacros({ brand, conversationId, simplrAccountPtrId }),
    {
      refetchOnWindowFocus: false,
      refetchInterval: false,
      cacheTime: FETCH_CHAT_MACROS.cacheTime,
      staleTime: FETCH_CHAT_MACROS.staleTime,
    }
  );
  useEffect(() => {
    if (macroQuery.data) {
      const { canDisplayMacros, mlRecommendedMacros, defaultSelectedMacro, originalResponseData } = macroQuery.data;

      mlEvents.trackChatMacrosFetched(originalResponseData);
      if (
        canDisplayMacros &&
        !isEmpty(mlRecommendedMacros) &&
        shouldChatMacrosDisplay(user, targetUserType, canDisplayMacros)
      ) {
        mlEvents.trackChatMacrosDisplayed(originalResponseData);
        setRecommendedMacros(mlRecommendedMacros);
        if (!isEmpty(defaultSelectedMacro)) {
          mlEvents.trackChatMacrosViewed(defaultSelectedMacro);
          setSelectedMacro(defaultSelectedMacro);
        }
      }
    }
  }, [macroQuery.data, targetUserType, user, conversationId, dispatch]);

  // RENDER/TAB SELECTION LOGIC

  useEffect(() => {
    if (canShowProductRecs) {
      // Once the conditions to show productRecs are met, this effect will automatically switch the tab to productRecs if it is already open
      if (chatUIIsOpen && chatUIStatus.activeTab !== chatUITabs.macros.tabName) {
        dispatch(setChatUIStatus({ isOpen: true, isOnscreen: true, activeTab: chatUITabs.productRecs.tabName }));
      }
    } else if (canShowMacros) {
      // This effect will automatically open the ChaUI to macros if they can be shown
      if (chatUIIsClosed) {
        dispatch(setChatUIStatus({ isOpen: true, isOnscreen: true, activeTab: chatUITabs.macros.tabName }));
      }
    }
  }, [chatUIStatus.activeTab, dispatch, canShowProductRecs, canShowMacros, chatUIIsClosed, chatUIIsOpen]);

  /** *** UTIL *****/

  // This function is used to determine which tab to show when the chatUI is manually opened, and not opened as a result of above conditions being met/effects running
  const chooseTabToOpen = () => {
    if (canShowProductRecs) {
      return chatUITabs.productRecs.tabName;
    }

    return chatUITabs.macros.tabName;
  };

  const determineChatUIHeight = () => {
    if (chatUIIsOpen) {
      return `height: 127px`;
    } else if (chatUIOnscreenClosed) {
      return `height: 26px`;
    }
  };

  /** ***  EVENT HANDLERS *****/

  const handleUIToggle = () => {
    if (chatUIIsOpen) {
      dispatch(setChatUIStatus({ isOpen: false, isOnscreen: true, activeTab: null }));
    } else {
      let currentTab = chooseTabToOpen();
      dispatch(setChatUIStatus({ isOpen: true, isOnscreen: true, activeTab: currentTab }));
    }
  };

  const handleTabSelection = (tabName) => {
    if (chatUIStatus.activeTab !== tabName) {
      dispatch(setChatUIStatus({ isOpen: true, isOnscreen: true, activeTab: tabName }));
    }
  };

  const handleSelectMacro = (macroId) => {
    const newSelectedMacro = recommendedMacros.find((macro) => macro.macro_article_id === macroId);
    mlEvents.trackChatMacrosViewed(newSelectedMacro);
    setSelectedMacro(newSelectedMacro);
  };

  const onCloseMacros = (source) => {
    mlEvents.trackChatMacrosClosed(source);
    dispatch(setChatUIStatus({ isOpen: false, isOnscreen: true, activeTab: null }));
  };

  const onMacroPersist = useCallback(async () => {
    mlEvents.trackChatMacrosCopied(selectedMacro, 'COPY_BUTTON');
    onMacroPersistNotify(selectedMacro);

    // if macro had chatProductRecsEnabled as true, update redux
    const chatProductRecsEnabled = get(selectedMacro, 'chatProductRecsEnabled', false);
    if (chatProductRecsEnabled) {
      dispatch(getMLRecommendedProducts(selectedMacro.text, true));
      dispatch(setChatProductRecMacroCopied(true));
    }

    // if macro had tags, add it to ticket;
    if (selectedMacro.tags.length) {
      const tagPath = selectedMacro.tags[0];
      const splitPath = util.splitTagPath(tagPath);
      for (let i = 0; i < splitPath.length; i++) {
        const tag = splitPath[i];
        await trackMacroWizardCategorySelected(tag);
      }
      await dispatch(setTagForTicket(tagPath));
    }
  }, [selectedMacro, onMacroPersistNotify, dispatch]);

  /** *** RENDER FUNCS *****/

  const renderChatUIContent = () => {
    switch (chatUIStatus.activeTab) {
      case chatUITabs.macros.tabName:
        return !isEmpty(recommendedMacros) ? (
          <ChatUIMacrosTab
            selectMacro={handleSelectMacro}
            selectedMacro={selectedMacro}
            recommendedMacros={recommendedMacros}
            toggleOpenState={handleUIToggle}
            onClose={onCloseMacros}
            onMacroPersist={onMacroPersist}
          />
        ) : (
          <ChatUIEmptyTab title={chatUITabs.macros.tabName} />
        );
      case chatUITabs.productRecs.tabName:
        return recommendedProductsExist || productsAlreadyRecommendedOnTicket ? (
          <ChatUIProductsTab />
        ) : (
          <ChatUIEmptyTab title={chatUITabs.productRecs.tabName} />
        );
      default:
        return null;
    }
  };

  return (
    <ChatUIContainer height={determineChatUIHeight()}>
      <ChatUIToolbar
        activeTab={chatUIStatus.activeTab}
        chatUIOpen={chatUIIsOpen}
        selectTab={handleTabSelection}
        toggleOpenState={handleUIToggle}
      />
      {chatUIIsOpen && <ChatUIContent>{renderChatUIContent()}</ChatUIContent>}
    </ChatUIContainer>
  );
};

ChatUI.propTypes = propTypes;

export const shouldChatMacrosDisplay = (user, targetUserType, toDisplay) => {
  return toDisplay && (targetUserType !== 'admin' || (targetUserType === 'admin' && user.isAdmin === true));
};

export default ChatUI;
